/**
 * Storage migrations (1.1.0): an administrator's managed mass move or copy of files from one storage location to
 * another, planned first (a dry run with an estimate), then run by the API's scheduler inside an optional transfer
 * window with concurrency, bandwidth and daily limits, paused on errors, and reviewed before the sources are removed.
 *
 * Request bodies (zod) and the DTOs the API answers with. The database columns are in migration 0078.
 */
import { z } from 'zod';
import { DEFAULT_SCHEDULE_TIMEZONE, isTimeZone } from './date-schedule.ts';
import { TransferWindowSchema, type TransferWindowPeriod } from './transfer-window.ts';

// ---------------------------------------------------------------- statuses and codes

export const MIGRATION_STATUSES = ['draft', 'planning', 'planned', 'running', 'paused', 'copying_done', 'removing', 'completed', 'cancelled', 'failed'] as const;
export type MigrationStatus = (typeof MIGRATION_STATUSES)[number];
/** At most one migration per (source, destination) pair in these (else 409 MIGRATION_ACTIVE). */
export const MIGRATION_ACTIVE_STATUSES = ['running', 'paused', 'removing'] as const satisfies readonly MigrationStatus[];
/** Finished: may be deleted (as may a draft). */
export const MIGRATION_FINISHED_STATUSES = ['completed', 'cancelled', 'failed'] as const satisfies readonly MigrationStatus[];

/** Why a migration is paused. offline reasons resume by themselves; the others are resumed by hand. */
export const MIGRATION_PAUSED_REASONS = ['manual', 'errors', 'destination_full', 'dest_fill', 'source_offline', 'dest_offline', 'dest_unavailable'] as const;
export type MigrationPausedReason = (typeof MIGRATION_PAUSED_REASONS)[number];
export const MIGRATION_AUTO_RESUME_REASONS = ['source_offline', 'dest_offline'] as const satisfies readonly MigrationPausedReason[];

/** Why a running migration queues nothing just now. */
export const MIGRATION_WAITING_REASONS = ['window_closed', 'daily_cap'] as const;
export type MigrationWaitingReason = (typeof MIGRATION_WAITING_REASONS)[number];

export const MIGRATION_ACTIONS = ['move', 'copy'] as const;
export type MigrationAction = (typeof MIGRATION_ACTIONS)[number];

/**
 * When a move removes the source files: after_review (the default: every file is copied and verified, then an
 * administrator reviews and confirms Remove sources), per_file (as each copy is verified), after_days (N days after
 * each copy is verified).
 */
export const MIGRATION_SOURCE_REMOVALS = ['after_review', 'per_file', 'after_days'] as const;
export type MigrationSourceRemoval = (typeof MIGRATION_SOURCE_REMOVALS)[number];

/** What happens to running transfers when the window closes. */
export const MIGRATION_WINDOW_END = ['finish_running', 'cancel_running'] as const;
export type MigrationWindowEnd = (typeof MIGRATION_WINDOW_END)[number];

/** Which files: originals; proxies and previews (derivatives); collateral (attachments). */
export const MIGRATION_FILE_KINDS = ['original', 'derivative', 'collateral'] as const;
export type MigrationFileKind = (typeof MIGRATION_FILE_KINDS)[number];

export const MIGRATION_ITEM_KINDS = ['representation', 'attachment'] as const;
export type MigrationItemKind = (typeof MIGRATION_ITEM_KINDS)[number];

export const MIGRATION_ITEM_STATUSES = ['pending', 'active', 'verified', 'removed', 'done', 'skipped', 'failed', 'cancelled'] as const;
export type MigrationItemStatus = (typeof MIGRATION_ITEM_STATUSES)[number];

/** An item's reason (skipped, failed, or why it was retried or finished the way it did). */
export const MIGRATION_ITEM_REASONS = [
  /** The file's only copy is in an archive tier: skipped and listed (no restore first). */
  'ORIGINAL_ARCHIVED',
  /** A copy: the destination already has the file. */
  'ALREADY_PRESENT',
  /** The file is no longer on the source. */
  'NOT_PRESENT',
  /** The asset was recycled or purged. */
  'ASSET_DELETED',
  /** Another transfer (a tiering rule's) moves the same file; tried again later. */
  'TRANSFER_ACTIVE',
  /** The item's transfer row is gone (job history removed, or the API stopped in between). */
  'INTERRUPTED',
  /** The transfer failed after every retry. */
  'TRANSFER_FAILED',
  /** An attachment's copy did not match its checksum. */
  'CHECKSUM_MISMATCH',
  /** Remove sources: the source copy was already gone. */
  'SOURCE_ALREADY_GONE',
  /** Remove sources: the destination copy is missing or its checksum differs, so the source was kept. */
  'DEST_NOT_VERIFIED',
  /** The migration was cancelled before the item finished. */
  'CANCELLED',
] as const;
export type MigrationItemReason = (typeof MIGRATION_ITEM_REASONS)[number];

/** Error codes the migration and transfer-window routes answer with. */
export const MIGRATION_ERROR_CODES = {
  /** 409: another migration between the same locations is running, paused or removing sources. */
  MIGRATION_ACTIVE: 'MIGRATION_ACTIVE',
  /** 428: Start (a move) and Remove sources need the source location's name typed. */
  CONFIRMATION_REQUIRED: 'CONFIRMATION_REQUIRED',
  /** 409: the action does not apply in the migration's status (e.g. Remove sources before copying_done). */
  MIGRATION_STATE: 'MIGRATION_STATE',
  /** 409: a transfer window used by a migration or a tiering rule cannot be deleted. */
  WINDOW_IN_USE: 'WINDOW_IN_USE',
  /** 409: a location (or an agent's location) used by a migration cannot be removed. */
  LOCATION_IN_MIGRATION: 'LOCATION_IN_MIGRATION',
} as const;

/** What the review step warns about. */
export const MIGRATION_WARNING_CODES = [
  /** The destination lacks room for everything planned. */
  'DESTINATION_ROOM',
  /** Enabled tiering rules use the source or the destination. */
  'TIERING_RULES',
  /** An agent at either end is too old to limit bandwidth. */
  'OLD_AGENT',
  /** The source is a cold (archive) tier. */
  'COLD_SOURCE',
  /** Copies inside one object store (copyFrom) are not limited by the bandwidth setting. */
  'UNTHROTTLED_COPY',
  /** Files added to the source after planning are not part of the migration. */
  'NEW_FILES_EXCLUDED',
] as const;
export type MigrationWarningCode = (typeof MIGRATION_WARNING_CODES)[number];

// ---------------------------------------------------------------- request bodies

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const Uuid = z.string().uuid();

/** Which files on the source location move. */
export const MigrationSelection = z.object({
  kinds: z.array(z.enum(MIGRATION_FILE_KINDS)).min(1).default(['original', 'derivative', 'collateral']),
  /** Asset classes (category ids); empty: every class. */
  categoryIds: z.array(Uuid).max(200).default([]),
  /** Ingest date range, local dates in `timezone`, inclusive; null: open. */
  ingestedFrom: z.string().regex(DATE, 'YYYY-MM-DD').nullable().default(null),
  ingestedTo: z.string().regex(DATE, 'YYYY-MM-DD').nullable().default(null),
  /** Only files not used for this many days (last access, or arrival when never used); null: any. */
  lastAccessOlderThanDays: z.number().int().min(0).max(36_500).nullable().default(null),
  timezone: z.string().min(1).max(64).refine(isTimeZone, 'Unknown time zone').default(DEFAULT_SCHEDULE_TIMEZONE),
}).refine((s) => !s.ingestedFrom || !s.ingestedTo || s.ingestedFrom <= s.ingestedTo, { message: 'ingestedFrom is after ingestedTo', path: ['ingestedTo'] });
export type MigrationSelection = z.infer<typeof MigrationSelection>;

const RetryFields = {
  /** Tries after the first failure. */
  attempts: z.number().int().min(0).max(10),
  /** The pause before each retry, in minutes; the last repeats. */
  backoffMinutes: z.array(z.number().int().min(1).max(10_080)).min(1).max(10),
};
export const MigrationRetry = z.object(RetryFields);
export type MigrationRetry = z.infer<typeof MigrationRetry>;

const LimitFields = {
  /** Transfers queued or running at once. */
  maxConcurrent: z.number().int().min(1).max(64),
  /** The whole migration's bandwidth, MB/s (10^6 bytes), shared by its transfers (at least 1 MB/s each); null: none. */
  maxBandwidthMBps: z.number().min(1).max(100_000).nullable(),
  maxFilesPerDay: z.number().int().min(1).nullable(),
  maxBytesPerDay: z.number().int().min(1).nullable(),
  /** Pause (dest_fill) when the destination is this full, percent; null: only when it is full. */
  stopAtDestPct: z.number().min(1).max(100).nullable(),
  retry: MigrationRetry,
  /** Pause (errors) when this percentage of the last 100 results failed (at least 20 results). */
  errorPausePct: z.number().min(1).max(100),
  windowEnd: z.enum(MIGRATION_WINDOW_END),
};

export const DEFAULT_MIGRATION_LIMITS = {
  maxConcurrent: 2,
  maxBandwidthMBps: null,
  maxFilesPerDay: null,
  maxBytesPerDay: null,
  stopAtDestPct: null,
  retry: { attempts: 3, backoffMinutes: [5, 30, 120] },
  errorPausePct: 20,
  windowEnd: 'finish_running',
} as const satisfies z.input<z.ZodObject<typeof LimitFields>>;

/** A limit set that holds together (null when it does): the bandwidth gives each transfer at least 1 MB/s. */
export function checkMigrationLimits(l: Pick<MigrationLimits, 'maxConcurrent' | 'maxBandwidthMBps'>): string | null {
  if (l.maxBandwidthMBps != null && l.maxBandwidthMBps < l.maxConcurrent) return 'The bandwidth must give each transfer at least 1 MB/s';
  return null;
}

export const MigrationLimits = z.object({
  maxConcurrent: LimitFields.maxConcurrent.default(DEFAULT_MIGRATION_LIMITS.maxConcurrent),
  maxBandwidthMBps: LimitFields.maxBandwidthMBps.default(null),
  maxFilesPerDay: LimitFields.maxFilesPerDay.default(null),
  maxBytesPerDay: LimitFields.maxBytesPerDay.default(null),
  stopAtDestPct: LimitFields.stopAtDestPct.default(null),
  retry: z.object({
    attempts: RetryFields.attempts.default(DEFAULT_MIGRATION_LIMITS.retry.attempts),
    backoffMinutes: RetryFields.backoffMinutes.default(() => [...DEFAULT_MIGRATION_LIMITS.retry.backoffMinutes]),
  }).prefault({}),
  errorPausePct: LimitFields.errorPausePct.default(DEFAULT_MIGRATION_LIMITS.errorPausePct),
  windowEnd: LimitFields.windowEnd.default(DEFAULT_MIGRATION_LIMITS.windowEnd),
}).superRefine((l, ctx) => {
  const problem = checkMigrationLimits(l);
  if (problem) ctx.addIssue({ code: 'custom', message: problem, path: ['maxBandwidthMBps'] });
});
export type MigrationLimits = z.infer<typeof MigrationLimits>;

/** Limits in a patch: only the fields given change (the server merges, then checks with checkMigrationLimits). */
export const MigrationLimitsPatch = z.object(LimitFields).partial();
export type MigrationLimitsPatch = z.infer<typeof MigrationLimitsPatch>;

const MigrationFields = z.object({
  name: z.string().trim().min(1).max(120),
  notes: z.string().max(4000).default(''),
  sourceLocationId: Uuid,
  destinationLocationId: Uuid,
  action: z.enum(MIGRATION_ACTIONS).default('move'),
  sourceRemoval: z.enum(MIGRATION_SOURCE_REMOVALS).default('after_review'),
  /** after_days: days after each file is verified. */
  removalAfterDays: z.number().int().min(1).max(3650).nullable().default(null),
  selection: MigrationSelection.prefault({}),
  limits: MigrationLimits.prefault({}),
  /** A transfer window; null: any time. */
  windowId: Uuid.nullable().default(null),
});

function checkRemoval(b: { action?: MigrationAction; sourceRemoval?: MigrationSourceRemoval; removalAfterDays?: number | null }, ctx: z.RefinementCtx) {
  if (b.action === 'move' && b.sourceRemoval === 'after_days' && b.removalAfterDays == null) {
    ctx.addIssue({ code: 'custom', message: 'Give the number of days', path: ['removalAfterDays'] });
  }
}

/** POST /api/v1/storage-migrations: a new migration, saved as a draft. */
export const MigrationBody = MigrationFields.superRefine((b, ctx) => {
  if (b.sourceLocationId === b.destinationLocationId) ctx.addIssue({ code: 'custom', message: 'The source and destination must differ', path: ['destinationLocationId'] });
  checkRemoval(b, ctx);
});
export type MigrationBody = z.infer<typeof MigrationBody>;

/**
 * PATCH /api/v1/storage-migrations/:id. In draft and planned everything but the locations; while running or paused
 * only name, notes, limits and windowId (the server answers 409 MIGRATION_STATE otherwise). A changed selection or
 * action sends a planned migration back to draft.
 */
export const MigrationPatch = z.object({
  name: MigrationFields.shape.name.optional(),
  notes: z.string().max(4000).optional(),
  action: z.enum(MIGRATION_ACTIONS).optional(),
  sourceRemoval: z.enum(MIGRATION_SOURCE_REMOVALS).optional(),
  removalAfterDays: z.number().int().min(1).max(3650).nullable().optional(),
  selection: MigrationSelection.optional(),
  limits: MigrationLimitsPatch.optional(),
  windowId: Uuid.nullable().optional(),
}).superRefine(checkRemoval);
export type MigrationPatch = z.infer<typeof MigrationPatch>;

/** POST /api/v1/storage-migrations/preview: live counts for the wizard's "what" step. */
export const MigrationPreviewBody = z.object({
  sourceLocationId: Uuid,
  destinationLocationId: Uuid,
  action: z.enum(MIGRATION_ACTIONS).default('move'),
  selection: MigrationSelection.prefault({}),
});
export type MigrationPreviewBody = z.infer<typeof MigrationPreviewBody>;

/** /start and /remove-sources: the source location's name, typed (a copy's start needs none). */
export const MigrationConfirmBody = z.object({ confirm: z.string().max(200).optional() });
export const MigrationPauseBody = z.object({ cancelRunning: z.boolean().default(false) });

/** POST and PUT /api/v1/transfer-windows. */
export const TransferWindowBody = TransferWindowSchema.extend({
  name: z.string().trim().min(1).max(80),
});
export type TransferWindowBody = z.infer<typeof TransferWindowBody>;

// ---------------------------------------------------------------- DTOs

export interface NamedRef {
  id: string;
  name: string;
}

export interface MigrationCountsDTO {
  files: number;
  bytes: number;
}

/** Files and bytes by outcome: total planned; done (copied and verified); failed; skipped; removed (sources removed). */
export interface MigrationCounters {
  total: MigrationCountsDTO;
  done: MigrationCountsDTO;
  failed: MigrationCountsDTO;
  skipped: MigrationCountsDTO;
  removed: MigrationCountsDTO;
}

export interface MigrationWarning {
  code: MigrationWarningCode;
  message: string;
  /** TIERING_RULES: the rules concerned. */
  rules?: NamedRef[];
}

/** The dry run, written when planning finishes. */
export interface MigrationPlanReport {
  plannedAt: string;
  /** What will move. */
  files: number;
  bytes: number;
  byKind: Record<MigrationFileKind, MigrationCountsDTO>;
  /** Skipped while planning, by reason (ORIGINAL_ARCHIVED, ALREADY_PRESENT, …). */
  skipped: Partial<Record<MigrationItemReason, MigrationCountsDTO>>;
  /** The largest files, largest first (at most 20). */
  largest: Array<{ assetId: string; objectKey: string; fileKind: MigrationFileKind; sizeBytes: number }>;
  destination: {
    /** Room PRISM may use there (quota or disk); null when unknown. */
    freeBytes: number | null;
    /** Whether everything fits; null when unknown. */
    enough: boolean | null;
  };
  estimate: {
    /** The rate assumed (the bandwidth limit, else a measured or default rate). */
    bytesPerSecond: number | null;
    /** Open time needed, in milliseconds. */
    workMs: number | null;
    /** When it would finish if started now; null when unknown or more than 400 days ahead. */
    finishAt: string | null;
    /** "about 9 nights (23:00–05:00)", "about 3 hours". */
    description: string | null;
  };
  warnings: MigrationWarning[];
}

/** POST /preview: counts for the selection as it stands (no items written). */
export interface MigrationPreview {
  files: number;
  bytes: number;
  byKind: Record<MigrationFileKind, MigrationCountsDTO>;
  skipped: Partial<Record<MigrationItemReason, MigrationCountsDTO>>;
  /** The count stopped at its statement timeout (5 s): the numbers are at least these. */
  partial: boolean;
}

export interface MigrationDTO {
  id: string;
  name: string;
  notes: string;
  source: NamedRef;
  destination: NamedRef;
  action: MigrationAction;
  sourceRemoval: MigrationSourceRemoval;
  removalAfterDays: number | null;
  selection: MigrationSelection;
  limits: MigrationLimits;
  /** The window with its time zone and periods, so the UI can show its hours and the next opening without a fetch. */
  window: (NamedRef & { description: string; timezone: string; periods: TransferWindowPeriod[] }) | null;
  status: MigrationStatus;
  pausedReason: MigrationPausedReason | null;
  waitingReason: MigrationWaitingReason | null;
  planReport: MigrationPlanReport | null;
  planError: string | null;
  counters: MigrationCounters;
  /** Transfers in flight now: running, queued (admitted), and queued waiting for admission (a free transfer slot). */
  inFlight: { running: number; queued: number; waitingAdmission: number };
  /** Today's counters for the daily caps (dayKey: the local date in the selection's time zone). */
  today: { dayKey: string | null; files: number; bytes: number };
  /** Measured rate (EWMA) while running; null before there is one. */
  bytesPerSecond: number | null;
  /** When it should finish at the current rate and window; null when unknown. */
  etaAt: string | null;
  /** The window as the scheduler last saw it; open is true without a window. */
  windowState: { open: boolean; nextChangeAt: string | null; openedAt: string | null; idleLastWindow: boolean };
  createdBy: { id: string; displayName: string } | null;
  startedBy: { id: string; displayName: string } | null;
  createdAt: string;
  updatedAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  lastTickAt: string | null;
}

export interface MigrationItemDTO {
  /** bigserial, as a string. */
  id: string;
  kind: MigrationItemKind;
  fileKind: MigrationFileKind;
  assetId: string;
  assetTitle: string | null;
  representationId: string | null;
  attachmentId: string | null;
  objectKey: string;
  sizeBytes: number;
  status: MigrationItemStatus;
  reason: MigrationItemReason | null;
  error: string | null;
  attempts: number;
  nextAttemptAt: string | null;
  transferId: string | null;
  queuedAt: string | null;
  verifiedAt: string | null;
  removeAfter: string | null;
  removedAt: string | null;
  finishedAt: string | null;
}

/** GET /items?status&cursor: keyset pages. */
export interface MigrationItemsPage {
  items: MigrationItemDTO[];
  nextCursor: string | null;
}

export interface TransferWindowDTO {
  id: string;
  name: string;
  timezone: string;
  periods: TransferWindowPeriod[];
  /** describeWindow's text. */
  description: string;
  /** What uses the window (it cannot be deleted while anything does). */
  usedBy: { migrations: NamedRef[]; rules: NamedRef[] };
  openNow: boolean;
  /** The next opening or closing within 8 days; null when none. */
  nextChangeAt: string | null;
  createdBy: { id: string; displayName: string } | null;
  createdAt: string;
  updatedAt: string;
}
